/**
 * Scripted, IP-safe fixture data for the FlowDemo component — ONE source set
 * shared across every screen so the demo is a true end-to-end flow with state
 * that persists across layers: connect a system → infer its schema (some fields
 * land "unrecognised") → map it into the golden customer (recognised fields
 * auto-map by concept; unrecognised ones you map by hand from the available
 * targets) → reconcile conflicts by lineage. The shape mirrors the real platform
 * (ConnectionRenderer / MappingRenderer). Field names come from providers' PUBLIC
 * API specs; classifications/confidences are the customer-facing output. No live
 * API calls — deterministic, identical wherever embedded.
 */
export type Protocol = 'REST' | 'GraphQL' | 'SOAP' | 'gRPC';

/** One inferred field, as the connection-review screen shows it. */
export interface DemoField {
  readonly apiName: string; // raw field name as it comes off the API
  readonly displayName: string; // humanised label Inferrex assigns
  readonly fieldType: string; // drives the colour-coded type badge
  readonly classification: string; // "what it means" (person · email, …)
  readonly confidence: number; // 0..1
  /**
   * Canonical concept this field resolves to (Inferrex's shared vocabulary).
   * Two systems sharing a concept is what lets ANY schema map to ANY other —
   * and is where reconciliation conflicts surface. '' = unrecognised: it lands
   * in the mapping screen for a human to map against the target by hand.
   */
  readonly concept: string;
  readonly transform?: string; // how it normalises to canonical (default identity)
  /**
   * Sensitive / regulated data (PII like a national id). Needs a human nod
   * BEFORE ingest regardless of how confident the classification is — distinct
   * from a low-confidence "low signal" flag, which is derived from `confidence`.
   */
  readonly sensitive?: boolean;
  /* Value lineage — why a system is the source of truth for this field. */
  readonly updatedAt?: string; // human "last changed" (e.g. "3h ago")
  readonly ageHours?: number; // same, numeric — for the "Freshest" rule
  readonly fill?: number; // 0..1 completeness — "most info" (the "Most complete" rule)
}

/** A connected system. One type used by every screen (infer / map / heal). */
export interface Source {
  readonly key: string;
  readonly name: string; // short, for chips / tabs
  readonly displayName: string; // e.g. "Stripe API"
  readonly baseUrl: string;
  readonly protocol: Protocol;
  readonly authMethod: string;
  readonly inferenceMethod: string;
  readonly noSpec?: boolean; // inferred by live probing, with no published spec
  readonly entity: string; // entity display name (e.g. "Customer")
  readonly overallConfidence: number;
  readonly fields: readonly DemoField[];
  /** Self-heal script: the field this system renames in a new API version. */
  readonly change: {
    readonly fromApi: string;
    readonly toApi: string;
    readonly displayName: string;
    readonly fieldType: string;
    readonly classification: string;
  };
}

/**
 * How conflicts resolve when two systems describe the same customer field —
 * i.e. why one system is the source of truth for that field. Each rule reads
 * off real lineage: when it last changed, how complete it is, how confidently
 * Inferrex classified it.
 */
export const AUTHORITY_RULES = ['Freshest', 'Most complete', 'Highest confidence', 'Keep both'] as const;

/** The unified customer record every system maps into. */
export interface GoldenField { readonly field: string; readonly label: string; readonly type: string }
export const GOLDEN_CUSTOMER: Record<string, GoldenField> = {
  email: { field: 'email', label: 'Email', type: 'email' },
  full_name: { field: 'full_name', label: 'Full name', type: 'text' },
  first_name: { field: 'first_name', label: 'First name', type: 'text' },
  last_name: { field: 'last_name', label: 'Last name', type: 'text' },
  phone: { field: 'phone', label: 'Phone', type: 'phone' },
  national_id: { field: 'national_id', label: 'National ID', type: 'identity' },
  company: { field: 'company', label: 'Company', type: 'text' },
  lifecycle: { field: 'lifecycle_stage', label: 'Lifecycle', type: 'enum' },
  country: { field: 'country', label: 'Country', type: 'location' },
  currency: { field: 'currency', label: 'Currency', type: 'currency' },
  created_at: { field: 'created_at', label: 'Created at', type: 'datetime' },
};
export const CONCEPT_ORDER: readonly string[] = [
  'email', 'full_name', 'first_name', 'last_name', 'phone', 'national_id',
  'company', 'lifecycle', 'country', 'currency', 'created_at',
];

/**
 * Five systems that all describe the same customer, across protocols — including
 * one undocumented "no-spec" system inferred by live probing, with a genuinely
 * unrecognised field (`seg_x`) that must be mapped by hand downstream.
 */
export const SOURCES: readonly Source[] = [
  {
    key: 'stripe', name: 'Stripe', displayName: 'Stripe API', baseUrl: 'https://api.stripe.com/v1',
    protocol: 'REST', authMethod: 'Bearer', inferenceMethod: 'OpenAPI spec + live probe', entity: 'Customer', overallConfidence: 0.96,
    fields: [
      { apiName: 'email', displayName: 'Email', fieldType: 'email', classification: 'person · email', confidence: 0.99, concept: 'email', updatedAt: '2d ago', ageHours: 48, fill: 0.99 },
      { apiName: 'name', displayName: 'Full name', fieldType: 'text', classification: 'person · full name', confidence: 0.96, concept: 'full_name', updatedAt: 'Mar 2024', ageHours: 1400, fill: 0.94 },
      { apiName: 'phone', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone', confidence: 0.94, concept: 'phone', updatedAt: '2d ago', ageHours: 48, fill: 0.61 },
      { apiName: 'default_currency', displayName: 'Currency', fieldType: 'currency', classification: 'money · currency', confidence: 0.98, concept: 'currency', transform: 'currency code', updatedAt: '2d ago', ageHours: 48, fill: 1.00 },
      { apiName: 'created', displayName: 'Created', fieldType: 'datetime', classification: 'time · created at', confidence: 0.95, concept: 'created_at', transform: 'unix → ISO 8601', updatedAt: 'at signup', ageHours: 99999, fill: 1.00 },
    ],
    change: { fromApi: 'created', toApi: 'created_at', displayName: 'Created', fieldType: 'datetime', classification: 'time · created at' },
  },
  {
    key: 'hubspot', name: 'HubSpot', displayName: 'HubSpot CRM', baseUrl: 'https://api.hubapi.com/crm/v3',
    protocol: 'REST', authMethod: 'Private app token', inferenceMethod: 'OpenAPI spec', entity: 'Contact', overallConfidence: 0.94,
    fields: [
      { apiName: 'email', displayName: 'Email', fieldType: 'email', classification: 'person · email', confidence: 0.98, concept: 'email', updatedAt: '3h ago', ageHours: 3, fill: 0.98 },
      { apiName: 'firstname', displayName: 'First name', fieldType: 'text', classification: 'person · first name', confidence: 0.95, concept: 'first_name', updatedAt: '3h ago', ageHours: 3, fill: 0.97 },
      { apiName: 'lastname', displayName: 'Last name', fieldType: 'text', classification: 'person · last name', confidence: 0.95, concept: 'last_name', updatedAt: '3h ago', ageHours: 3, fill: 0.92 },
      { apiName: 'phone', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone', confidence: 0.92, concept: 'phone', updatedAt: '3h ago', ageHours: 3, fill: 0.93 },
      { apiName: 'company', displayName: 'Company', fieldType: 'text', classification: 'org · company', confidence: 0.88, concept: 'company', updatedAt: '1mo ago', ageHours: 720, fill: 0.74 },
      { apiName: 'lifecyclestage', displayName: 'Lifecycle', fieldType: 'enum', classification: 'category · lifecycle', confidence: 0.90, concept: 'lifecycle', transform: 'enum → status', updatedAt: '3h ago', ageHours: 3, fill: 0.96 },
    ],
    change: { fromApi: 'lifecyclestage', toApi: 'lifecycle_stage', displayName: 'Lifecycle', fieldType: 'enum', classification: 'category · lifecycle' },
  },
  {
    key: 'shopify', name: 'Shopify', displayName: 'Shopify Admin', baseUrl: 'https://shop.myshopify.com/admin/graphql',
    protocol: 'GraphQL', authMethod: 'Access token', inferenceMethod: 'Introspection + SDL', entity: 'Customer', overallConfidence: 0.93,
    fields: [
      { apiName: 'email', displayName: 'Email', fieldType: 'email', classification: 'person · email', confidence: 0.99, concept: 'email', updatedAt: '1d ago', ageHours: 24, fill: 0.97 },
      { apiName: 'displayName', displayName: 'Display name', fieldType: 'text', classification: 'person · full name', confidence: 0.91, concept: 'full_name', updatedAt: '1d ago', ageHours: 24, fill: 0.80 },
      { apiName: 'phone', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone', confidence: 0.90, concept: 'phone', updatedAt: '1d ago', ageHours: 24, fill: 0.70 },
      { apiName: 'countryCodeV2', displayName: 'Country', fieldType: 'location', classification: 'location · country', confidence: 0.85, concept: 'country', updatedAt: '1d ago', ageHours: 24, fill: 0.88 },
      { apiName: 'createdAt', displayName: 'Created', fieldType: 'datetime', classification: 'time · created at', confidence: 0.97, concept: 'created_at', transform: 'ISO 8601', updatedAt: 'at signup', ageHours: 99999, fill: 1.00 },
    ],
    change: { fromApi: 'countryCodeV2', toApi: 'country_code', displayName: 'Country', fieldType: 'location', classification: 'location · country' },
  },
  {
    key: 'salesforce', name: 'Salesforce', displayName: 'Salesforce', baseUrl: 'https://login.salesforce.com/services/Soap/u/59',
    protocol: 'SOAP', authMethod: 'WS-Security', inferenceMethod: 'WSDL + envelope probe', entity: 'Contact', overallConfidence: 0.94,
    fields: [
      { apiName: 'Email', displayName: 'Email', fieldType: 'email', classification: 'person · email', confidence: 0.97, concept: 'email', updatedAt: '5d ago', ageHours: 120, fill: 0.95 },
      { apiName: 'FirstName', displayName: 'First name', fieldType: 'text', classification: 'person · first name', confidence: 0.96, concept: 'first_name', updatedAt: '5d ago', ageHours: 120, fill: 0.99 },
      { apiName: 'LastName', displayName: 'Last name', fieldType: 'text', classification: 'person · last name', confidence: 0.96, concept: 'last_name', updatedAt: '5d ago', ageHours: 120, fill: 0.99 },
      { apiName: 'Phone', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone', confidence: 0.93, concept: 'phone', updatedAt: '5d ago', ageHours: 120, fill: 0.85 },
      { apiName: 'MailingCountry', displayName: 'Country', fieldType: 'location', classification: 'location · country', confidence: 0.84, concept: 'country', updatedAt: '5d ago', ageHours: 120, fill: 0.90 },
      { apiName: 'AccountName', displayName: 'Company', fieldType: 'text', classification: 'org · company', confidence: 0.86, concept: 'company', updatedAt: '2w ago', ageHours: 336, fill: 0.92 },
    ],
    change: { fromApi: 'Phone', toApi: 'PhoneNumber', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone' },
  },
  {
    key: 'legacy', name: 'Legacy CRM', displayName: 'Legacy CRM (undocumented)', baseUrl: 'https://api.acme-internal.io/crm',
    protocol: 'REST', authMethod: 'API key', inferenceMethod: 'live probing — no spec', noSpec: true, entity: 'Customer', overallConfidence: 0.74,
    fields: [
      { apiName: 'eml', displayName: 'Email', fieldType: 'email', classification: 'person · email', confidence: 0.88, concept: 'email', updatedAt: '1w ago', ageHours: 168, fill: 0.90 },
      { apiName: 'cust_nm', displayName: 'Full name', fieldType: 'text', classification: 'person · full name', confidence: 0.83, concept: 'full_name', updatedAt: '1w ago', ageHours: 168, fill: 0.88 },
      { apiName: 'tel_no', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone', confidence: 0.85, concept: 'phone', updatedAt: '1w ago', ageHours: 168, fill: 0.66 },
      { apiName: 'nino', displayName: 'NI number', fieldType: 'identity', classification: 'identity · national id', confidence: 0.93, concept: 'national_id', sensitive: true, updatedAt: '1w ago', ageHours: 168, fill: 0.80 },
      { apiName: 'signup_ts', displayName: 'Created', fieldType: 'datetime', classification: 'time · created at (epoch ms)', confidence: 0.80, concept: 'created_at', transform: 'epoch ms → ISO 8601', updatedAt: 'at signup', ageHours: 99999, fill: 1.00 },
      { apiName: 'seg_x', displayName: 'seg_x', fieldType: 'object', classification: 'unrecognised — sampled values inconclusive', confidence: 0.41, concept: '', updatedAt: '1w ago', ageHours: 168, fill: 0.55 },
    ],
    change: { fromApi: 'tel_no', toApi: 'telephone', displayName: 'Phone', fieldType: 'phone', classification: 'person · phone' },
  },
];

/** Stack-coverage script: a typical SMB stack, each synced live with a snapshot. */
export interface StackTool {
  readonly name: string;
  readonly protocol: Protocol;
  readonly entity: string;
  readonly fields: readonly { readonly apiName: string; readonly fieldType: string }[];
}
export const STACK_TOOLS: readonly StackTool[] = [
  { name: 'Salesforce', protocol: 'REST', entity: 'Account', fields: [{ apiName: 'Name', fieldType: 'text' }, { apiName: 'Website', fieldType: 'url' }, { apiName: 'AnnualRevenue', fieldType: 'currency' }] },
  { name: 'HubSpot', protocol: 'REST', entity: 'Contact', fields: [{ apiName: 'email', fieldType: 'email' }, { apiName: 'firstname', fieldType: 'text' }, { apiName: 'createdate', fieldType: 'datetime' }] },
  { name: 'Xero', protocol: 'REST', entity: 'Invoice', fields: [{ apiName: 'Total', fieldType: 'currency' }, { apiName: 'DueDate', fieldType: 'date' }, { apiName: 'Status', fieldType: 'enum' }] },
  { name: 'Stripe', protocol: 'REST', entity: 'Customer', fields: [{ apiName: 'email', fieldType: 'email' }, { apiName: 'currency', fieldType: 'currency' }, { apiName: 'created', fieldType: 'datetime' }] },
  { name: 'Shopify', protocol: 'GraphQL', entity: 'Customer', fields: [{ apiName: 'email', fieldType: 'email' }, { apiName: 'displayName', fieldType: 'text' }, { apiName: 'phone', fieldType: 'phone' }] },
  { name: 'Zendesk', protocol: 'REST', entity: 'Ticket', fields: [{ apiName: 'subject', fieldType: 'text' }, { apiName: 'status', fieldType: 'enum' }, { apiName: 'created_at', fieldType: 'datetime' }] },
];

/** Pre-filled sample for the "infer a schema yourself" box. */
export const INFER_SAMPLE = `{
  "email": "ada@acme.io",
  "full_name": "Ada Lovelace",
  "mrr_amount": 4999,
  "signup_date": "2024-03-01",
  "is_active": true,
  "country": "GB"
}`;

/* ── InferField widget: classify a single field by name + sample value. ── */
export interface InferredField {
  readonly key: string;
  readonly fieldType: string;
  readonly classification: string;
  readonly confidence: number;
}

export function inferField(key: string, value: unknown): InferredField {
  const k = key.toLowerCase();
  const v = typeof value === 'string' ? value : '';
  const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
  const isUrl = /^https?:\/\//i.test(v);
  const isIso = /^\d{4}-\d{2}-\d{2}/.test(v);
  const numeric = typeof value === 'number' || (/^-?\d+(\.\d+)?$/.test(v) && v !== '');
  const out = (fieldType: string, classification: string, confidence: number): InferredField => ({ key, fieldType, classification, confidence });
  if (/e-?mail/.test(k) || isEmail) return out('email', 'person · email', isEmail ? 0.99 : 0.95);
  if (/(phone|tel|mobile|msisdn)/.test(k)) return out('phone', 'person · phone', 0.94);
  if (/currency/.test(k)) return out('currency', 'money · currency', 0.96);
  if (/(amount|price|total|cost|salary|revenue|balance|fee|mrr|arr)/.test(k)) return out('currency', 'money · amount', 0.9);
  if (/(created|updated|_at$|timestamp|signup|date|dob|birth)/.test(k) || isIso) return out('datetime', 'time · date', isIso ? 0.96 : 0.9);
  if (typeof value === 'boolean' || /^(is|has|can|should)_|_(enabled|active)$|^(enabled|active|verified)$/.test(k)) return out('boolean', 'flag · boolean', 0.92);
  if (/(url|link|website|href|domain)/.test(k) || isUrl) return out('url', 'web · url', isUrl ? 0.97 : 0.9);
  if (/country/.test(k)) return out('location', 'location · country', 0.86);
  if (/(city|town|address|postcode|postal|zip|county|state|region)/.test(k)) return out('location', 'location · place', 0.84);
  if (/(^id$|_id$|uuid|guid|_ref$|reference)/.test(k)) return out('id', 'reference · id', 0.9);
  if (/(first_?name|last_?name|full_?name|display_?name|^name$|title|contact)/.test(k)) return out('text', 'person · name', 0.88);
  if (/(status|stage|type$|category|kind|state|tier)/.test(k)) return out('enum', 'category · status', 0.82);
  if (numeric) return out('number', 'number · numeric', 0.85);
  if (value !== null && typeof value === 'object') return out('object', 'extension · object', 0.6);
  if (v.length > 0) return out('text', 'text · freeform', 0.7);
  return out('object', 'unrecognised', 0.45);
}
